import { useRouteLoaderData } from "react-router";
import { fetchApi } from "./api";

export interface NavItem {
  id: string;
  parentId: string | null;
  label: string;
  href: string;
  description: string | null;
  icon: string | null;
  opensInNewTab: boolean;
}

export interface NavNode extends NavItem {
  children: NavItem[];
}

export interface NavMenu {
  location: string;
  items: NavNode[];
}

/** Public settings, keyed exactly as the `Setting.key` column stores them. */
export type SiteSettings = Record<string, unknown>;

export interface SiteContext {
  header: NavMenu;
  footerPrimary: NavMenu;
  footerSecondary: NavMenu;
  legal: NavMenu;
  settings: SiteSettings;
}

const EMPTY_MENU = (location: string): NavMenu => ({ location, items: [] });

/**
 * Site chrome, fetched once in the root loader.
 *
 * Every request is allowed to fail independently. If the settings endpoint is
 * briefly unavailable the header still renders with whatever navigation
 * returned — a site that loses its phone number is bad, one that returns a 500
 * because of it is worse.
 */
export async function loadSiteContext(): Promise<SiteContext> {
  const [header, footerPrimary, footerSecondary, legal, settings] = await Promise.all([
    fetchApi<NavMenu>("/public/navigation/HEADER").catch(() => EMPTY_MENU("HEADER")),
    fetchApi<NavMenu>("/public/navigation/FOOTER_PRIMARY").catch(() =>
      EMPTY_MENU("FOOTER_PRIMARY"),
    ),
    fetchApi<NavMenu>("/public/navigation/FOOTER_SECONDARY").catch(() =>
      EMPTY_MENU("FOOTER_SECONDARY"),
    ),
    fetchApi<NavMenu>("/public/navigation/LEGAL").catch(() => EMPTY_MENU("LEGAL")),
    fetchApi<SiteSettings>("/public/settings").catch(() => ({}) as SiteSettings),
  ]);

  return { header, footerPrimary, footerSecondary, legal, settings };
}

/**
 * Read the site context from anywhere in the tree.
 *
 * Returns a usable shape rather than `undefined` when the root loader has not
 * run — which happens inside the root error boundary, exactly where a component
 * that assumed data would crash a second time and lose the error page.
 */
export function useSite(): SiteContext {
  const data = useRouteLoaderData("root") as { site?: SiteContext } | undefined;

  return (
    data?.site ?? {
      header: EMPTY_MENU("HEADER"),
      footerPrimary: EMPTY_MENU("FOOTER_PRIMARY"),
      footerSecondary: EMPTY_MENU("FOOTER_SECONDARY"),
      legal: EMPTY_MENU("LEGAL"),
      settings: {},
    }
  );
}

/** A setting as a string, with a fallback for the "not configured yet" case. */
export function settingString(settings: SiteSettings, key: string, fallback = ""): string {
  const value = settings[key];
  return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

export function settingList<T>(settings: SiteSettings, key: string): T[] {
  const value = settings[key];
  return Array.isArray(value) ? (value as T[]) : [];
}

/** Absolute site origin, used for canonical URLs, Open Graph and the sitemap. */
export const SITE_URL = (
  typeof process !== "undefined" && process.env.SITE_URL
    ? process.env.SITE_URL
    : (import.meta.env.VITE_SITE_URL ?? "http://localhost:3000")
).replace(/\/+$/, "");

export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Office hours, for any page that wants to show them. */
export function useOfficeHours(): { days: string; hours: string }[] {
  const { settings } = useSite();
  return settingList<{ days: string; hours: string }>(settings, "contact.officeHours");
}
